import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import {
  selectDoraGeneralInfo,
  selectDoraGeneralInfoError,
  selectDoraGeneralInfoLoading,
  setDoraGeneralInfo,
  setError,
  setLoading,
} from '../store/generalInfo/generalInfoSlice.ts';
import {
  createDoraGeneralInfo,
  listDoraGeneralInfo,
  updateDoraGeneralInfo,
} from '../../../api.ts';
import { DoraGeneralInfo } from '../models/doraGeneralInfo.ts';
import { ENTITY_TYPE_OPTIONS } from '../constants/entityTypes.ts';
import CountrySelector from '../../../components/CountrySelector.tsx';

// Helper function to format API error details
const formatErrorDetail = (detail: any): string => {
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    // FastAPI validation errors are arrays of error objects
    return detail.map((err: any) => {
      const field = err.loc ? err.loc.slice(1).join('.') : 'unknown';
      return `${field}: ${err.msg}`;
    }).join('; ');
  }
  if (typeof detail === 'object' && detail !== null) {
    return JSON.stringify(detail);
  }
  return 'An unknown error occurred';
};

const GeneralInfoForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const generalInfo = useAppSelector(selectDoraGeneralInfo);
  const loading = useAppSelector(selectDoraGeneralInfoLoading);
  const error = useAppSelector(selectDoraGeneralInfoError);
  const currentOrg = useAppSelector(selectCurrentOrganization);

  const [formState, setFormState] = useState<Partial<DoraGeneralInfo>>({
    lei: '',
    entity_name: '',
    country_code: '',
    entity_type: '',
    competent_authority: '',
    reporting_date: '',
  });

  const canEdit = currentOrg?.role && currentOrg.role !== 'viewer';

  // Load general info on mount
  useEffect(() => {
    const loadGeneralInfo = async () => {
      try {
        dispatch(setLoading(true));
        const data = await listDoraGeneralInfo();
        // Since there's only one record per org, take the first one
        if (data.length > 0) {
          dispatch(setDoraGeneralInfo(data[0]));
        } else {
          dispatch(setDoraGeneralInfo(null));
        }
      } catch (err: any) {
        console.error('Failed to load general info', err);
        const errorMsg = err?.response?.data?.detail
          ? formatErrorDetail(err.response.data.detail)
          : 'Failed to load general information';
        dispatch(setError(errorMsg));
      } finally {
        dispatch(setLoading(false));
      }
    };

    loadGeneralInfo();
  }, [dispatch]);

  // Update form state when general info changes
  useEffect(() => {
    if (generalInfo) {
      setFormState({
        lei: generalInfo.lei || '',
        entity_name: generalInfo.entity_name || '',
        country_code: generalInfo.country_code || '',
        entity_type: generalInfo.entity_type || '',
        competent_authority: generalInfo.competent_authority || '',
        reporting_date: generalInfo.reporting_date || '',
      });
    }
  }, [generalInfo]);

  const handleSave = async () => {
    // Validate required fields
    if (!formState.lei || !formState.entity_name || !formState.country_code || !formState.entity_type) {
      dispatch(setError('LEI, Entity Name, Country Code, and Entity Type are required'));
      return;
    }

    // Validate LEI length
    if (formState.lei.length !== 20) {
      dispatch(setError('LEI must be exactly 20 characters'));
      return;
    }

    // Validate country code length
    if (formState.country_code.length !== 2) {
      dispatch(setError('Country Code must be exactly 2 characters (ISO 3166-1 alpha-2)'));
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      // Prepare data, converting empty strings to null for optional fields
      const dataToSave = {
        ...formState,
        competent_authority: formState.competent_authority || null,
        reporting_date: formState.reporting_date || null,
      };

      let savedInfo: DoraGeneralInfo;

      if (generalInfo?.id) {
        // Update existing record
        savedInfo = await updateDoraGeneralInfo(generalInfo.id, dataToSave);
      } else {
        // Create new record
        savedInfo = await createDoraGeneralInfo(dataToSave as Omit<DoraGeneralInfo, 'id'>);
      }

      dispatch(setDoraGeneralInfo(savedInfo));
    } catch (err: any) {
      console.error('Failed to save general info', err);
      const errorMsg = err?.response?.data?.detail
        ? formatErrorDetail(err.response.data.detail)
        : 'Failed to save general information';
      dispatch(setError(errorMsg));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        General Information (DORA B_01.01)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        General information on the financial entity maintaining and updating the register of information.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="LEI (Legal Entity Identifier)"
              value={formState.lei}
              onChange={e => setFormState(prev => ({ ...prev, lei: e.target.value }))}
              required
              fullWidth
              disabled={!canEdit || loading}
              inputProps={{ maxLength: 20, minLength: 20 }}
              helperText="20-character ISO 17442 code (e.g., 549300XXXXXXXXXXXXX1)"
            />

            <TextField
              label="Entity Name"
              value={formState.entity_name}
              onChange={e => setFormState(prev => ({ ...prev, entity_name: e.target.value }))}
              required
              fullWidth
              disabled={!canEdit || loading}
              helperText="Legal name of the entity"
            />

            <CountrySelector
              value={formState.country_code}
              onChange={code => setFormState(prev => ({ ...prev, country_code: code }))}
              label="Country"
              required
              disabled={!canEdit || loading}
              helperText="Select the country where the entity is registered"
            />

            <FormControl fullWidth required disabled={!canEdit || loading}>
              <InputLabel id="entity-type-label">Entity Type</InputLabel>
              <Select
                labelId="entity-type-label"
                value={formState.entity_type}
                label="Entity Type"
                onChange={e => setFormState(prev => ({ ...prev, entity_type: e.target.value }))}
              >
                {ENTITY_TYPE_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Competent Authority (Optional)"
              value={formState.competent_authority}
              onChange={e => setFormState(prev => ({ ...prev, competent_authority: e.target.value }))}
              fullWidth
              disabled={!canEdit || loading}
              helperText="Competent authority according to Article 46 of Regulation (EU) 2022/2554"
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Reporting Date (Optional)"
                type="date"
                value={formState.reporting_date}
                onChange={e => setFormState(prev => ({ ...prev, reporting_date: e.target.value }))}
                fullWidth
                disabled={!canEdit || loading}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setFormState(prev => ({ ...prev, reporting_date: today }));
                }}
                disabled={!canEdit || loading}
                sx={{ minWidth: '100px', mt: 0 }}
              >
                Today
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!canEdit || loading}
              >
                Save
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeneralInfoForm;
